import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { motion } from 'framer-motion';

interface TasteProfileProgressProps {
  currentStep: number;
  totalSteps: number;
  completedSteps: string[];
}

export function TasteProfileProgress({ currentStep, totalSteps, completedSteps }: TasteProfileProgressProps) {
  const progress = (currentStep / totalSteps) * 100;

  const getStepStatus = (step: number) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepTitle = (step: number) => {
    const titles = [
      'Dietary & Restrictions',
      'Goals & Macros',
      'Household & Equipment',
      'Preferences & Pantry',
      'Units & Measurements',
      'Notifications & Email',
      'Recipe DNA',
      'Review & Confirm'
    ];
    return titles[step - 1] || `Step ${step}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
    >
      <Card className="bg-gradient-to-r from-coral/5 to-spice/5 border-coral/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Taste Profile Setup</h2>
            <span className="text-sm text-muted-foreground">
              {currentStep} of {totalSteps}
            </span>
          </div>

          <Progress value={progress} className="h-3 mb-6" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
              const status = getStepStatus(step);
              const title = getStepTitle(step);

              return (
                <div
                  key={step}
                  className={`flex flex-col items-center p-3 rounded-lg transition-all ${
                    status === 'completed'
                      ? 'bg-coral text-white'
                      : status === 'current'
                      ? 'bg-coral/20 border-2 border-coral'
                      : 'bg-stone/10'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium mb-1 ${
                    status === 'completed'
                      ? 'bg-white text-coral'
                      : status === 'current'
                      ? 'bg-coral text-white'
                      : 'bg-stone/30 text-stone/600'
                  }`}>
                    {status === 'completed' ? '✓' : step}
                  </div>
                  <span className={`text-xs text-center leading-tight ${
                    status === 'current' ? 'font-medium' : ''
                  }`}>
                    {title}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}